/**
 * FmTopNav — Top navigation bar from catalog (7usro.8)
 *
 * Renders the primary nav jobs from the catalog as a horizontal row.
 * At most five jobs render directly on desktop; remaining catalogued jobs use
 * the one governed overflow menu. Each job either opens a menu or navigates
 * to one canonical workspace.
 *
 * GOVERNING: uses FmRouteCatalog, not hardcoded lists.
 * Active state from FmNavActiveContext (catalog ownership).
 * Desktop: menu jobs open a mega-menu; action jobs execute directly.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  FmRouteCatalogData,
  FmRouteCatalogEntry,
  FmTopNavJobEntry,
  PrimaryNavId,
} from './FmRouteCatalog';
import { getJobGroups, getGroupRoutes } from './FmRouteCatalog';
import { useFmNavActive } from './FmNavActiveContext.types';
import FmMegaMenu from './FmMegaMenu';
import { FmMenu, type FmMenuItem } from '@/components/ui/FmMenu';
import './FmTopNav.css';

export interface FmTopNavProps {
  catalog: FmRouteCatalogData;
  resolveRoutePath?: (route: FmRouteCatalogEntry) => string;
  /** Direct owner jobs enter their governed chooser independently of menu-route context. */
  resolveDirectJobPath?: (route: FmRouteCatalogEntry) => string;
  onClose?: () => void;
  /** Localized coming-soon badge label, forwarded to the mega-menu. */
  comingSoonLabel?: string;
  /** Localized label for the one governed desktop overflow menu. */
  overflowLabel: string;
  testId?: string;
}

const FmOverflowJobs: React.FC<{
  jobs: FmTopNavJobEntry[];
  label: string;
  activeJobId: PrimaryNavId | undefined;
  /** A primary job's menu is open, so the current page's job recedes. */
  primaryMenuOpen: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActivate: (job: FmTopNavJobEntry) => void;
}> = ({ jobs, label, activeJobId, primaryMenuOpen, open, onOpenChange, onActivate }) => {
  const items: FmMenuItem[] = jobs.map((job) => ({
    key: job.id,
    label: job.label,
    'data-testid': `nav-overflow-job-${job.id}`,
  }));
  if (items.length === 0) return null;
  const active = jobs.some((job) => job.id === activeJobId);
  return (
    <FmMenu
      items={items}
      trigger="hover"
      placement="bottomRight"
      menuTestId="top-nav-overflow-menu"
      open={open}
      onOpenChange={onOpenChange}
      onItemClick={({ key }) => {
        const job = jobs.find((candidate) => candidate.id === key);
        if (job) onActivate(job);
      }}
    >
      <button
        type="button"
        className={[
          'fm-top-nav__job',
          'fm-top-nav__overflow-trigger',
          active && !primaryMenuOpen && 'fm-top-nav__job--active',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid="nav-job-overflow"
        aria-current={active ? 'page' : undefined}
      >
        {label}
      </button>
    </FmMenu>
  );
};

function resolveOpenMenuModel(
  catalog: FmRouteCatalogData,
  openJobId: PrimaryNavId | undefined,
  resolveRoutePath: (route: FmRouteCatalogEntry) => string,
) {
  const openJob = openJobId ? catalog.topJobs.find((job) => job.id === openJobId) : undefined;
  const groupsWithRoutes = openJobId
    ? getJobGroups(catalog, openJobId)
        .map((group) => ({
          group,
          routes: getGroupRoutes(catalog, group.id).map((route) => ({
            ...route,
            path: resolveRoutePath(route),
          })),
        }))
        .filter((entry) => entry.routes.length > 0)
    : [];
  const promoCard = openJob
    ? catalog.promoCards.find((card) => card.id === openJob.promoCardId)
    : undefined;
  const promoTargetRoute = promoCard?.action.routeId
    ? catalog.routes.find((route) => route.id === promoCard.action.routeId)
    : undefined;
  return {
    groupsWithRoutes,
    promoCard,
    promoComingSoon: promoTargetRoute?.availability === 'coming-soon',
  };
}

const FmPrimaryJobs: React.FC<{
  jobs: FmTopNavJobEntry[];
  activeJobId: PrimaryNavId | undefined;
  activeRouteId: string | undefined;
  openJobId: PrimaryNavId | undefined;
  menuModel: ReturnType<typeof resolveOpenMenuModel>;
  comingSoonLabel?: string;
  onOpenJob: (jobId: PrimaryNavId) => void;
  onCloseMenu: () => void;
  onActivate: (job: FmTopNavJobEntry) => void;
  onRouteClick: (routeId: string, path: string) => void;
  onPromoAction: (promoCardId: string) => void;
}> = ({
  jobs,
  activeJobId,
  activeRouteId,
  openJobId,
  menuModel,
  comingSoonLabel,
  onOpenJob,
  onCloseMenu,
  onActivate,
  onRouteClick,
  onPromoAction,
}) => (
  <>
    {jobs.map((job) => {
      const opensMenu = job.activation.kind === 'menu';
      // One highlighted job at a time: while any menu is open the bar marks the
      // job whose panel is showing; aria-current still names the current page.
      const showsActive = activeJobId === job.id && (openJobId === undefined || openJobId === job.id);
      return (
        <div
          key={job.id}
          className="fm-top-nav__job-wrapper"
          onMouseLeave={onCloseMenu}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) onCloseMenu();
          }}
        >
          <button
            type="button"
            className={[
              'fm-top-nav__job',
              showsActive && 'fm-top-nav__job--active',
              openJobId === job.id && 'fm-top-nav__job--open',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onActivate(job)}
            onMouseEnter={() => {
              if (opensMenu) onOpenJob(job.id);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onCloseMenu();
            }}
            data-testid={`nav-job-${job.id}`}
            aria-expanded={opensMenu ? openJobId === job.id : undefined}
            aria-haspopup={opensMenu ? 'menu' : undefined}
            aria-current={activeJobId === job.id ? 'page' : undefined}
          >
            {job.label}
          </button>

          {opensMenu && openJobId === job.id && menuModel.groupsWithRoutes.length > 0 ? (
            <FmMegaMenu
              groups={menuModel.groupsWithRoutes}
              promoCard={
                menuModel.promoCard
                  ? {
                      heading: menuModel.promoCard.heading,
                      body: menuModel.promoCard.body,
                      actionLabel: menuModel.promoCard.action.label,
                      actionHref: menuModel.promoCard.action.href,
                      onAction: () => onPromoAction(menuModel.promoCard!.id),
                      badge: menuModel.promoCard.badge,
                      comingSoon: menuModel.promoComingSoon,
                    }
                  : undefined
              }
              activeRouteId={activeRouteId}
              onRouteClick={onRouteClick}
              onClose={onCloseMenu}
              comingSoonLabel={comingSoonLabel}
              testId={`mega-menu-${job.id}`}
            />
          ) : null}
        </div>
      );
    })}
  </>
);

/**
 * FmTopNav — renders top navigation jobs + mega-menu.
 * Wraps FmMegaMenu and orchestrates job/menu open state.
 */
export const FmTopNav: React.FC<FmTopNavProps> = ({
  catalog,
  resolveRoutePath = (route) => route.path,
  resolveDirectJobPath = resolveRoutePath,
  onClose,
  comingSoonLabel,
  overflowLabel,
  testId = 'fm-top-nav',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useFmNavActive();
  const [openJobId, setOpenJobId] = useState<PrimaryNavId | undefined>();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  const activePrimaryNavId = state.activePrimaryNavId;
  const primaryJobs = catalog.topJobs.filter((job) => job.desktopPlacement === 'primary');
  const overflowJobs = catalog.topJobs.filter((job) => job.desktopPlacement === 'overflow');

  useEffect(() => {
    setOpenJobId(undefined);
    setOverflowOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!openJobId) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpenJobId(undefined);
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        navRef.current?.querySelector<HTMLButtonElement>('.fm-top-nav__job--open')?.focus();
        setOpenJobId(undefined);
      }
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    document.addEventListener('keydown', dismissOnEscape, true);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside, true);
      document.removeEventListener('keydown', dismissOnEscape, true);
    };
  }, [openJobId]);

  const menuModel = resolveOpenMenuModel(catalog, openJobId, resolveRoutePath);

  const handleJobActivate = (job: FmTopNavJobEntry) => {
    const activation = job.activation;
    if (activation.kind === 'menu') {
      // Pointer entry may already have opened the menu before the click lands.
      // Keep click/Enter idempotently open; leaving or Escape owns dismissal.
      setOverflowOpen(false);
      setOpenJobId(job.id);
      return;
    }
    setOpenJobId(undefined);
    const route = catalog.routes.find((candidate) => candidate.id === activation.routeId);
    if (!route || route.availability === 'coming-soon') return;
    navigate(resolveDirectJobPath(route));
    onClose?.();
  };

  const handleRouteClick = (_routeId: string, path: string) => {
    navigate(path);
    setOpenJobId(undefined);
    onClose?.();
  };

  const handlePromoCardAction = (promoCardId: string) => {
    const card = catalog.promoCards.find((c) => c.id === promoCardId);
    if (!card?.action.routeId) return;
    const route = catalog.routes.find((r) => r.id === card.action.routeId);
    // Never navigate to a coming-soon / unresolvable target — the promo CTA is
    // rendered disabled in that case, but guard the handler too (c9tzf).
    if (!route || route.availability === 'coming-soon') return;
    const path = resolveRoutePath(route);
    if (path) {
      navigate(path);
      setOpenJobId(undefined);
    }
  };

  return (
    <nav ref={navRef} className="fm-top-nav" data-testid={testId}>
      <div className="fm-top-nav__items">
        <FmPrimaryJobs
          jobs={primaryJobs}
          activeJobId={activePrimaryNavId}
          activeRouteId={state.activeRouteId}
          openJobId={openJobId}
          menuModel={menuModel}
          comingSoonLabel={comingSoonLabel}
          onOpenJob={(jobId) => {
            setOverflowOpen(false);
            setOpenJobId(jobId);
          }}
          onCloseMenu={() => setOpenJobId(undefined)}
          onActivate={handleJobActivate}
          onRouteClick={handleRouteClick}
          onPromoAction={handlePromoCardAction}
        />
        <FmOverflowJobs
          jobs={overflowJobs}
          label={overflowLabel}
          activeJobId={activePrimaryNavId}
          primaryMenuOpen={openJobId !== undefined}
          open={overflowOpen}
          onOpenChange={(open) => {
            setOverflowOpen(open);
            if (open) setOpenJobId(undefined);
          }}
          onActivate={handleJobActivate}
        />
      </div>
    </nav>
  );
};

export default FmTopNav;
